/// <amd-module name="Wit/Settings/BoardsExternalIntegration/utils/ConnectionUtil" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { EmptyGuidString, equals, localeFormat, newGuid } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Uri } from "VSS/Platform/Util/Url";
import { ConnectedServiceClientName, IConnectedServiceRestClient } from "Wit/Clients/ConnectedService/Client/RestClient/ConnectedService";
import { AuthRequest } from "Wit/Clients/ConnectedService/Types/Contracts/ConnectedService";
import {
    ExternalConnection,
    ExternalConnectionData,
    ExternalConnectionOperation,
    ExternalConnectionProvisionResult,
    ExternalConnectionRequest,
    GitHubUnifiedInstallationRequest,
    GitHubUnifiedInstallationResponse,
    GitHubUser,
    GitHubUserRequest,
    OperationType,
    SaveExternalConnectionRequest
} from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { getServiceEndpointClient, IServiceEndpointRestClient } from "Wit/Clients/ServiceEndpoint/Client/RestClient/ServiceEndpoint";
import { EndpointAuthorization, ServiceEndpoint } from "Wit/Clients/ServiceEndpoint/Types/Contracts/ServiceEndpoint";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { AuthData, UserAuthorization } from "Wit/Settings/BoardsExternalIntegration/contracts/Models";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import {
    GitHubIntegrationCIActions,
    publishGitHubIntegrationTelemetryWithPageContext,
    publishGitHubIntegrationTraceWithPageContext
} from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";

export function getProviderKey(enterpriseUrl?: string): string {
    return enterpriseUrl ? new Uri(enterpriseUrl).host : GitHubConnectionConstants.GitHubProviderKey;
}

export function isValidEnterpriseUrl(enterpriseUrl: string): boolean {
    let isValidUrl = true;

    if (enterpriseUrl) {
        const uri = new Uri(enterpriseUrl);
        isValidUrl = (equals(uri.scheme, "http", true) || equals(uri.scheme, "https", true)) && !!uri.host;
    }

    return isValidUrl;
}

export function findExistingConnectionById(existingServiceConnections: ExternalConnection[], connectionId: string): ExternalConnection | undefined {
    if (!existingServiceConnections || existingServiceConnections.length <= 0) {
        return undefined;
    }
    return existingServiceConnections.find(item => equals(item.id, connectionId, true /* ignorecase */));
}

export function findExistingGitHubAppConnectionByOrgName(
    existingServiceConnections: ExternalConnection[],
    orgName: string
): ExternalConnection | undefined {
    if (!existingServiceConnections || existingServiceConnections.length <= 0) {
        return undefined;
    }
    // Find connection by orgName + Authorization scheme
    return existingServiceConnections.find(item => {
        const isAppConnectionActive = !item.statusMessage && item.serviceEndpoint.isEndpointValid;
        return (
            isAppConnectionActive &&
            equals(item.serviceEndpoint.gitHubHandle, orgName, true /* ignorecase */) &&
            equals(item.serviceEndpoint.authorizationScheme, GitHubConnectionConstants.GitHubAppScheme, true /* ignorecase */)
        );
    });
}

export function findExistingConnectionByGitHubHandle(
    existingServiceConnections: ExternalConnection[],
    gitHubHandle: string,
    enterpriseUrl?: string
): ExternalConnection | undefined {
    if (!existingServiceConnections || existingServiceConnections.length <= 0) {
        return undefined;
    }

    // Find connection by GitHubHandle + ProviderKey (enterprise url host or github.com)
    const providerKey = getProviderKey(enterpriseUrl);
    return existingServiceConnections.find(item => {
        return (
            equals(item.serviceEndpoint.gitHubHandle, gitHubHandle, true /* ignorecase */) &&
            equals(item.providerKey, providerKey, true /* ignorecase */) &&
            item.serviceEndpoint.authorizationScheme !==
                GitHubConnectionConstants.GitHubAppScheme /* ignore GitHub App ones to permit add OAuth/PAT on top of GH app */
        );
    });
}

export async function createEnterpriseCredentialsRequest(
    pageContext: IVssPageContext,
    username: string,
    password: string,
    existingConnections: ExternalConnection[],
    enterpriseUrl: string,
    acceptUntrustedSsl?: boolean
): Promise<ServiceEndpoint> {
    return _createRequest(
        pageContext,
        GitHubConnectionConstants.EnterpriseCredentialsScheme,
        { username, password },
        existingConnections,
        Resources.InValidCredentials,
        enterpriseUrl,
        acceptUntrustedSsl
    );
}

export async function createPatRequest(
    pageContext: IVssPageContext,
    accessToken: string,
    existingConnections: ExternalConnection[],
    acceptUntrustedSsl?: boolean
): Promise<ServiceEndpoint> {
    return _createRequest(
        pageContext,
        GitHubConnectionConstants.PersonalAccessTokenScheme,
        { token: accessToken },
        existingConnections,
        Resources.InValidPAT,
        undefined,
        acceptUntrustedSsl
    );
}

export async function createEnterprisePatRequest(
    pageContext: IVssPageContext,
    accessToken: string,
    existingConnections: ExternalConnection[],
    enterpriseUrl: string,
    acceptUntrustedSsl?: boolean
): Promise<ServiceEndpoint> {
    return _createRequest(
        pageContext,
        GitHubConnectionConstants.EnterprisePersonalAccessTokenScheme,
        { token: accessToken },
        existingConnections,
        Resources.InValidPAT,
        enterpriseUrl,
        acceptUntrustedSsl
    );
}

async function _createRequest(
    pageContext: IVssPageContext,
    scheme: string,
    data: AuthData,
    existingConnections: ExternalConnection[],
    errorMessage: string,
    enterpriseUrl?: string,
    acceptUntrustedSsl?: boolean
): Promise<ServiceEndpoint> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const providerId = "ms.vss-work-web.github-user-data-provider";
    const gitHubUser = await contributionService.getDataAsync<GitHubUser, GitHubUserRequest>(
        providerId,
        {
            accessToken: data.token,
            username: data.username,
            password: data.password,
            acceptUntrustedSsl,
            enterpriseUrl
        } as GitHubUserRequest,
        true
    );

    if (!gitHubUser || gitHubUser.errorMessage || !gitHubUser.login) {
        throw new Error(errorMessage);
    }

    const existingConnection = findExistingConnectionByGitHubHandle(existingConnections, gitHubUser.login, enterpriseUrl);
    if (existingConnection) {
        const errorMessage = localeFormat(Resources.DuplicateConnectionError, getFriendlyConnectionName(existingConnection));
        throw new Error(errorMessage);
    }

    let serviceEndpoint = _createServiceEndpoint(
        scheme,
        data,
        gitHubUser.login,
        undefined /* avatarUrl */,
        undefined /* configurationId */,
        enterpriseUrl,
        acceptUntrustedSsl
    );

    const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const pageData = pageService.getData();
    // tslint:disable-next-line:no-non-null-assertion
    const projectId = pageData!.project!.id;
    const serviceEndpointClient: IServiceEndpointRestClient = getServiceEndpointClient(pageContext);
    serviceEndpoint = await serviceEndpointClient.createServiceEndpoint(serviceEndpoint, projectId);

    return serviceEndpoint;
}

function _createServiceEndpoint(
    scheme: string,
    data: AuthData,
    gitHubHandle: string,
    avatarUrl?: string,
    configurationId?: string,
    enterpriseUrl?: string,
    acceptUntrustedSsl?: boolean
): ServiceEndpoint {
    const isGitHubConnection = !enterpriseUrl;

    const userAuth: UserAuthorization = {
        ...data,
        scheme: scheme,
        gitHubHandle: gitHubHandle,
        avatarUrl: avatarUrl && decodeURIComponent(avatarUrl),
        configurationId: configurationId
    } as UserAuthorization;

    if (isGitHubConnection) {
        return _createGitHubServiceEndpoint(userAuth);
    } else {
        return _createGitHubEnterpriseServiceEndpoint(userAuth, enterpriseUrl!, acceptUntrustedSsl);
    }
}

function _createGitHubServiceEndpoint(userAuthorization: UserAuthorization): ServiceEndpoint {
    const authorizationInfo: EndpointAuthorization = _createAuthorizationInfo(userAuthorization);

    const data: { [key: string]: string } = {
        AvatarUrl: userAuthorization.avatarUrl,
        GitHubHandle: userAuthorization.gitHubHandle
    };

    return {
        type: GitHubConnectionConstants.GitHubBoardsServiceEndpointType,
        url: GitHubConnectionConstants.ServiceProviderUrl,
        authorization: authorizationInfo,
        data: data,
        name: newGuid()
    } as ServiceEndpoint;
}

function _createGitHubEnterpriseServiceEndpoint(
    userAuthorization: UserAuthorization,
    url: string,
    acceptUntrustedSsl: boolean = false
): ServiceEndpoint {
    const authorizationInfo: EndpointAuthorization = _createAuthorizationInfo(userAuthorization);

    const data: { [key: string]: any } = {
        GitHubHandle: userAuthorization.gitHubHandle,
        acceptUntrustedCerts: acceptUntrustedSsl
    };

    // tslint:disable-next-line:no-unnecessary-local-variable
    const serviceEndpoint = {
        authorization: authorizationInfo,
        name: newGuid(),
        type: GitHubConnectionConstants.EnterpriseServiceEndpointType,
        data: data,
        url: url
    } as ServiceEndpoint;

    return serviceEndpoint;
}

/**
 * Create Authorization for non-OAuth connections
 */
function _createAuthorizationInfo(userAuthorization: UserAuthorization): EndpointAuthorization {
    const authorizationInfo: EndpointAuthorization = {
        scheme: userAuthorization.scheme
    } as EndpointAuthorization;

    if (userAuthorization.scheme === GitHubConnectionConstants.PersonalAccessTokenScheme) {
        authorizationInfo.parameters = {
            accessToken: userAuthorization.token!
        };
    } else if (userAuthorization.scheme === GitHubConnectionConstants.EnterprisePersonalAccessTokenScheme) {
        authorizationInfo.parameters = {
            apitoken: userAuthorization.token!
        };
    } else if (userAuthorization.scheme === GitHubConnectionConstants.EnterpriseCredentialsScheme) {
        authorizationInfo.parameters = {
            username: userAuthorization.username!,
            password: userAuthorization.password!
        };
    }

    return authorizationInfo;
}

/**
 * get recommended connection
 */
export async function getConnectionRecommendation(
    pageContext: IVssPageContext,
    connection: ExternalConnection,
    orgName: string,
    onPartialRepoProvisionError?: (error: string) => void
): Promise<GitHubUnifiedInstallationResponse> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    const result = await contributionService.getDataAsync<GitHubUnifiedInstallationResponse, GitHubUnifiedInstallationRequest>(
        "ms.vss-work-web.github-unified-installation-experience-data-provider",
        {
            orgName: orgName,
            externalRepositoryExternalIds: connection.externalGitRepos.map(r => r.id),
            existingConnectionId: connection.id
        } as GitHubUnifiedInstallationRequest,
        true
    );

    if (!result) {
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, Resources.FailedToSaveConnection_EmptyResult));
    }

    onPartialRepoProvisionError && onPartialRepoProvisionError(result.errorMessage);
    return result;
}

/**
 * Create or update the connection for both Oauth and App
 * @param installationId used for github app install and oauth combined flow
 * @param onPartialRepoProvisionError used to output partial provision error for some of the selected repos may not be successfully provisioned.
 */
export async function createOrUpdateConnection(
    pageContext: IVssPageContext,
    connection: ExternalConnection,
    isGitHubApp: boolean,
    onPartialRepoProvisionError?: (error: string) => void
): Promise<ExternalConnection> {
    const operation = connection.id ? OperationType.Update : OperationType.Create;
    const result = await performConnectionChanges(pageContext, connection, operation, isGitHubApp);

    // Validate result
    if (!result) {
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, Resources.FailedToSaveConnection_EmptyResult));
    } else if (result.errorMessage) {
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, result.errorMessage));
    } else if (!result.provisionedConnection) {
        let errorMessage = Resources.FailedToSaveConnection_EmptyConnectionData;
        for (const itemResult of result.repoProvisionResult) {
            // in the case connection is empty, per-repo result shall still exist.
            if (itemResult.message) {
                errorMessage = itemResult.message;
                break; // take first error only - this is not perfect but typically good enough for user.
            }
        }
        publishGitHubIntegrationTraceWithPageContext(
            pageContext,
            `Create or update connection returned empty connection, inner error message is: ${errorMessage}`
        );
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, errorMessage));
    } else if (!result.provisionedConnection.externalGitRepos) {
        publishGitHubIntegrationTraceWithPageContext(pageContext, "Create or update connection returned no repo.");
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, Resources.FailedToSaveConnection_NoReposInConnection));
    }

    // Check for repo provision errors
    const failedRepos = result.repoProvisionResult.filter(res => !res.isSuccess);
    if (connection.externalGitRepos.length === failedRepos.length) {
        publishGitHubIntegrationTraceWithPageContext(pageContext, "Create or update connection returned provision error for all of the repos.");
        throw new Error(localeFormat(Resources.FailedToSaveConnectionWithMessage, Resources.FailedToSaveConnection_NoReposInConnection));
    } else if (failedRepos.length > 0) {
        // Partial provision error - Do not throw, just log telemetry and show the error on the main list page
        publishGitHubIntegrationTraceWithPageContext(pageContext, "Create or update connection failed for some of the repos");
        const error = localeFormat(
            Resources.FailedToSaveConnectionWithMessage,
            localeFormat(Resources.FailedToSaveConnection_SomeReposFailedToAdd, failedRepos.join())
        );
        onPartialRepoProvisionError && onPartialRepoProvisionError(error);
    }

    return result.provisionedConnection;
}

/**
 * Remove connection for both Oauth and App
 */
export async function removeConnection(pageContext: IVssPageContext, connection: ExternalConnection): Promise<void> {
    const result = await performConnectionChanges(pageContext, connection, OperationType.Delete);

    // Validate result
    if (!result) {
        throw new Error(localeFormat(Resources.FailedToRemoveConnectionWithMessage, Resources.FailedToRemoveConnection_EmptyResult));
    } else if (result.errorMessage) {
        throw new Error(localeFormat(Resources.FailedToRemoveConnectionWithMessage, result.errorMessage));
    }

    publishGitHubIntegrationTelemetryWithPageContext(pageContext, GitHubIntegrationCIActions.ADMIN_REMOVE_CONNECTION);
}

const AZURE_BOARDS_SAVE_EXTERNAL_CONNECTION_DATA_PROVIDER_ID = "ms.vss-work-web.azure-boards-save-external-connection-data-provider";
async function performConnectionChanges(
    pageContext: IVssPageContext,
    connection: ExternalConnection,
    operation: OperationType,
    isGitHubApp?: boolean
): Promise<ExternalConnectionProvisionResult | undefined> {
    const { providerKey, serviceEndpoint, name: connectionName, id: connectionId, externalGitRepos } = connection;
    const serviceEndpointId = serviceEndpoint && serviceEndpoint.id;

    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const result = await contributionService.getDataAsync<ExternalConnectionProvisionResult, SaveExternalConnectionRequest>(
        AZURE_BOARDS_SAVE_EXTERNAL_CONNECTION_DATA_PROVIDER_ID,
        {
            externalConnection: {
                serviceEndpointId,
                connectionName,
                connectionId,
                operation,
                externalRepositoryExternalIds: externalGitRepos.map(r => r.id),
                providerKey: providerKey,
                isGitHubApp: !!isGitHubApp
            } as ExternalConnectionOperation
        },
        true
    );

    // Common trace message
    if (!result) {
        publishGitHubIntegrationTraceWithPageContext(pageContext, `${AZURE_BOARDS_SAVE_EXTERNAL_CONNECTION_DATA_PROVIDER_ID} returned empty result`, {
            operation: operation,
            providerKey,
            serviceEndpointId,
            connectionId,
            isGitHubApp: !!isGitHubApp
        });
    } else if (result.errorMessage) {
        publishGitHubIntegrationTraceWithPageContext(
            pageContext,
            `${AZURE_BOARDS_SAVE_EXTERNAL_CONNECTION_DATA_PROVIDER_ID} returend error: ${result.errorMessage}`
        );
    }

    return result;
}

export async function fetchConnectionsData(
    pageContext: IVssPageContext,
    includeInvalidConnections: boolean = false
): Promise<ExternalConnectionData> {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    return (await contributionService.getDataAsync<ExternalConnectionData>(
        "ms.vss-work-web.azure-boards-external-connection-data-provider",
        {
            includeInvalidConnections
        } as ExternalConnectionRequest,
        true
    )) as ExternalConnectionData;
}

export function getFriendlyConnectionName(connection: ExternalConnection): string {
    const serviceEndpoint = connection.serviceEndpoint;
    const connectionName = serviceEndpoint && (serviceEndpoint.gitHubHandle || serviceEndpoint.name);
    return connectionName;
}

export function createAuthRequest(pageContext: IVssPageContext, onError: (error: Error) => void, existingConnectionId?: string): void {
    const connectedServiceClient = pageContext.getRestClient<IConnectedServiceRestClient>(ConnectedServiceClientName);
    const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
    const pageData = pageService.getData();
    // tslint:disable-next-line:no-non-null-assertion
    const projectId = pageData!.project!.id;
    let queryParams = `redirectUrl=${encodeURIComponent(window.location.href)}&${GitHubConnectionConstants.UseWellKnownStrongBoxLocationKey}=true`;
    if (existingConnectionId) {
        queryParams = `${queryParams}&${GitHubConnectionConstants.ExistingConnectionId}=${existingConnectionId}`;
    }

    // tslint:disable-next-line:no-non-null-assertion
    connectedServiceClient
        .createAuthRequest(
            undefined!,
            projectId,
            GitHubConnectionConstants.GitHubBoardsServiceEndpointType,
            EmptyGuidString,
            GitHubConnectionConstants.OAuthScope,
            queryParams
        )
        .then((authRequest: AuthRequest) => {
            if (authRequest.errorMessage) {
                onError?.(new Error(authRequest.errorMessage));
                return;
            }
            window.open(authRequest.url, "_self");
        })
        .catch((error: unknown) => {
            if (error instanceof Error) {
                onError?.(error);
            } else {
                onError?.(new Error("" + error));
            }
        });
}
